import ConfirmDialog from '@components/Common/ConfirmDialog';
import DetailPageHeader from '@components/Common/DetailPageHeader';
import DetailSection from '@components/Common/DetailSection';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import Loader from '@components/Common/Loader';
import SelectFormField from '@components/Common/SelectFormField';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Container,
    FormControlLabel,
    Grid,
    Switch,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminUser, UserRole } from '../index';
import { ROLE_OPTIONS } from '@components/Common/Utils/roles';

import './index.scss';


interface Props {
    mode: 'create' | 'edit';
    user?: AdminUser;
    userId?: string;
    onSaveSuccess: () => void;
}

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    role: UserRole;
    isActive: boolean;
    password: string;
}

const UserCreateEditPopup: React.FC<Props> = ({ mode, user, userId, onSaveSuccess }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.email ?? '',
        // HR is the default only for a new user. Editing a user with no role must not pre-fill HR:
        // saving would demote an account the login controller treats as Super Admin.
        role: (user ? user.role ?? '' : 'HR') as UserRole,
        isActive: user?.isActive !== false,
        password: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const handleChange = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!formData.firstName.trim()) errs.firstName = 'First name is required';
        if (!formData.lastName.trim()) errs.lastName = 'Last name is required';
        if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
            errs.email = 'Valid email is required';
        }
        if (!formData.role) errs.role = 'Role is required';
        if (mode === 'create' && !formData.password) {
            errs.password = 'Password is required';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        setError(null);
        try {
            if (mode === 'create') {
                await axios.post('/api/users', {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    role: formData.role,
                    isActive: formData.isActive,
                    password: formData.password,
                });
            } else {
                const id = user?._id ?? userId;
                const payload: Record<string, any> = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    role: formData.role,
                    isActive: formData.isActive,
                };
                if (formData.password) payload.password = formData.password;
                await axios.put(`/api/users/${id}`, payload);
            }
            onSaveSuccess();
        } catch {
            setError(`Failed to ${mode === 'create' ? 'create' : 'update'} user. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const id = user?._id ?? userId;
        if (!id) return;
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await axios.delete(`/api/users/${id}`);
            navigate('/user-rbac');
        } catch (err: any) {
            setDeleteError(err?.response?.data?.message ?? 'Failed to delete user. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="detail-page-container">
            <DetailPageHeader
                title={mode === 'create' ? 'New User' : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Edit User'}
                isEditMode={true}
                canEdit={true}
                isSaving={loading}
                onEdit={() => navigate('/user-rbac')}
                onSave={handleSubmit}
                menuItems={mode === 'edit' ? [
                    {
                        label: 'Delete User',
                        icon: <DeleteIcon fontSize="small" />,
                        onClick: () => setDeleteDialogOpen(true),
                        color: 'error',
                        dividerBefore: true,
                    },
                ] : []}
            />

            <Container className="detail-page-content" disableGutters maxWidth="xl">
                {loading && <Loader />}
                {error && <ErrorMessage message={error} />}

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <DetailSection title="Personal Information">
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormField
                                        required
                                        label="First Name"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormField
                                        required
                                        label="Last Name"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <FormField
                                        required
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Grid>
                            </Grid>
                        </DetailSection>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <DetailSection title="Access & Role">
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 8 }}>
                                    <SelectFormField
                                        required
                                        label="Role"
                                        name="role"
                                        options={ROLE_OPTIONS}
                                        value={formData.role}
                                        onChange={handleChange}
                                        error={!!errors.role}
                                        helperText={errors.role}
                                    />
                                </Grid>
                                {/* Bottom-aligned to the Role control beside it. `sx={{ mt: 1 }}`
                                    was a guess at the height of that field's label, so the switch
                                    sat a few pixels off the input's centre line and moved again
                                    whenever the label wrapped. */}
                                <Grid size={{ xs: 12, sm: 4 }} className="cc-user-form__toggle">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.isActive}
                                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                            />
                                        }
                                        label="Active"
                                    />
                                </Grid>
                            </Grid>
                        </DetailSection>
                    </Grid>

                    {/*
                        The password card is a third item in the SAME grid, not a loose block
                        underneath it.

                        It used to sit outside the container with `sx={{ maxWidth: '50%' }}`, and
                        50% of the row is not the same width as a grid column: the column is
                        `50% - half the gutter`. So the card below was one gutter wider than the
                        card above it and its right edge overshot by 8px, which is exactly the
                        kind of near-miss that reads as sloppy without being obviously wrong.
                        Wrapping as a grid item makes the geometry identical by construction
                        rather than by arithmetic, and drops a layout `sx` at the same time.
                    */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <DetailSection title={mode === 'create' ? 'Set Password' : 'Reset Password'}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <FormField
                                        required={mode === 'create'}
                                        label={mode === 'create' ? 'Password' : 'New Password (leave blank to keep current)'}
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        error={!!errors.password}
                                        helperText={errors.password}
                                    />
                                </Grid>
                            </Grid>
                        </DetailSection>
                    </Grid>
                </Grid>
            </Container>

            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete this user?"
                body={
                    <>
                        <strong>{user?.firstName} {user?.lastName}</strong> loses access to the
                        console immediately and the account cannot be restored.
                        {deleteError && <><br /><br />{deleteError}</>}
                    </>
                }
                busy={deleteLoading}
                onConfirm={handleDelete}
                onCancel={() => { setDeleteDialogOpen(false); setDeleteError(null); }}
            />
        </div>
    );
};

export default UserCreateEditPopup;
